import { Button, Col, Input, Row, Spin, Table, Tabs } from "antd";
import React, { useEffect, useState } from "react";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table/interface";
import { Link } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { adminContext } from "../../Common/PageRoute";
import AddPractice from "./AddPractices";
import { PageHeader } from "../../Common/MainLayout";
import DailyPracticeStats from "../../Services/DailyPracticeStats";
import { ConfirmationModal } from "../../Common/ConfirmationModal";
import CleanCatchService from "../../Services/CleanCatchService";
import CustomRangePicker from "../../Common/Custom Components/Ant design/RangePicker";
import { useUser } from "../../Context/UserContext";

const { Search } = Input;

interface ListDataType {
  key: React.Key;
  id: number;
  name: string;
  phoneNumber: string;
  email: string;
  createdBy: any;
  updatedBy: any;
  inActive?: boolean;
  practices: any[];
}
interface ListData {
  items: ListDataType[];
  totalItems: number;
  practices: any[];
}

const categories = {
  Expenses: [
    { key: "aml", title: "AML", width: 80 },
    { key: "amL_Biometric", title: "AML-Biometric", width: 130 },
    { key: "incoming", title: "Incoming", width: 100 },
    { key: "outgoing", title: "Outgoing", width: 100 },
    { key: "emails", title: "Emails", width: 100 },
    { key: "storageUses", title: "Storage Uses", width: 120 },
  ],
  Uses: [
    { key: "cs", title: "CS", width: 70 },
    { key: "cf", title: "CF", width: 70 },
    { key: "accountsReport", title: "Accounts", width: 90 },
    { key: "ct", title: "CT", width: 70 },
    { key: "vat", title: "VAT", width: 70 },
    { key: "eps", title: "EPS", width: 70 },
    { key: "fps", title: "FPS", width: 70 },
    { key: "cis", title: "CIS", width: 70 },
    { key: "sA100", title: "SA100", width: 80 },
    { key: "sA800", title: "SA800", width: 80 },
    { key: "sA900", title: "SA900", width: 80 },
  ],
  Financial: [
    { key: "activeClients", title: "Active Clients", width: 110 },
    { key: "stripeCount", title: "Stripe Count", width: 110 },
    { key: "stripeTotalAmount", title: "Stripe Amt.", width: 110 },
    { key: "goCardlessCount", title: "GoCardless Count", width: 140 },
    { key: "goCardlessTotalAmount", title: "GoCardless Amt.", width: 140 },
    { key: "invoicesCount", title: "Invoices Count", width: 120 },
    { key: "invoicesAmount", title: "Invoices Amt.", width: 120 },
    { key: "receiptsCount", title: "Receipts Count", width: 120 },
    { key: "receiptsAmount", title: "Receipts Amt.", width: 120 },
  ],
};

type CategoryKey = keyof typeof categories;


const DEFAULT_SORT = {
  sortCol: "practiceId",
  sortDir: "ascend" as "ascend" | "descend",
};

const Practices = () => {
  const { openNotification } = React.useContext(adminContext);
  const { isSubAdmin } = useUser();
  const isViewOnly = isSubAdmin();

  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState<any>({ type: "", params: {} });
  const [dailyPracticeStatsData, setDailyPracticeStatsData] = useState<any>([]);
  const [activeTab, setActiveTab] = useState<CategoryKey>("Expenses");

  // Single shared sort state — no per-tab sorting
  const [currentSort, setCurrentSort] = useState<{
    sortCol: string;
    sortDir: "ascend" | "descend";
  }>(DEFAULT_SORT);

  const [pagingItems, setPagingItems] = React.useState({
    totalRecords: 0,
    currentPage: 1,
    pageSize: 15,
  });

  const [statsFilters, setStatsFilters] = useState<{
    search: string;
    status: string;
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
  }>({
    search: "",
    status: "",
    fromDate: dayjs().startOf("month"),
    toDate: dayjs(),
  });

  const fetchStatsData = async (
    pageSize?: number,
    currentPage?: number,
    sortCol?: string,
    sortDir?: string,
  ) => {
    setLoading(true);
    try {
      const page = currentPage ?? pagingItems.currentPage;
      const size = pageSize ?? pagingItems.pageSize;
      const start = (page - 1) * size;
      const col = sortCol ?? currentSort.sortCol;
      const dir = sortDir ?? currentSort.sortDir;

      const params: any = {
        start,
        length: size,
        sortCol: col,
        sortDir: dir,
      };

      if (statsFilters.search) params.search = statsFilters.search;
      if (statsFilters.fromDate) {
        params.fromDate = statsFilters.fromDate.startOf("day").toISOString();
      }
      if (statsFilters.toDate) {
        params.toDate = statsFilters.toDate.endOf("day").toISOString();
      }

      const res = await DailyPracticeStats.getDailyPracticeStats(params);
      setDailyPracticeStatsData(res || []);

      if (res?.result?.totalRecords) {
        setPagingItems((prev) => ({
          ...prev,
          totalRecords: res.result?.totalRecords,
        }));
      }
    } catch (error) {
      console.error(error);
      openNotification("error", "Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch on search / date filter change — reset to page 1
  useEffect(() => {
    setPagingItems((prev) => ({ ...prev, currentPage: 1 }));
    fetchStatsData(pagingItems.pageSize, 1, currentSort.sortCol, currentSort.sortDir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsFilters.search, statsFilters.fromDate, statsFilters.toDate]);

  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    let sortCol = DEFAULT_SORT.sortCol;
    let sortDir: "ascend" | "descend" = "ascend";

    if (sorter?.order) {
      sortDir = sorter.order === "descend" ? "descend" : "ascend";
    }

    if (sorter?.field) {
      let field = sorter.field;
      if (Array.isArray(field)) field = field.join(",");
      if (typeof field === "string" && field.includes(",")) {
        sortCol = field.split(",")[1];
      } else {
        sortCol = field;
      }
    }

    const newPage = pagination.current;
    const newPageSize = pagination.pageSize;

    setPagingItems({
      currentPage: newPage,
      pageSize: newPageSize,
      totalRecords: pagination.total,
    });

    setCurrentSort({ sortCol, sortDir });
    fetchStatsData(newPageSize, newPage, sortCol, sortDir);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key as CategoryKey);
  };

  const openAddPractice = () => {
    setDialog({ type: "addPractice", params: {} });
  };

  const commonColumns: ColumnsType<ListData> = [
    {
      title: "S.No",
      key: "sno",
      width: 30,
      render: (_: any, __: any, index: number) =>
        (pagingItems.currentPage - 1) * pagingItems.pageSize + index + 1,
    },
    {
      title: "Tenant Id",
      sorter: true,
      showSorterTooltip: false,
      dataIndex: "practiceId",
      key: "practiceId",
      width: 100,
      sortOrder:
        currentSort.sortCol === "practiceId" ? currentSort.sortDir : undefined,
      render: (id) => `Tenant - ${id}`,
    },
    {
      title: "Name",
      sorter: true,
      showSorterTooltip: false,
      dataIndex: "practiceName",
      key: "practiceName",
      width: 190,
      sortOrder:
        currentSort.sortCol === "practiceName" ? currentSort.sortDir : undefined,
      render: (text: string, record: any) => (
        <Link
          to={`/practices/${record.practiceId}`}
          state={{ practiceName: record.practiceName }}
        >
          {text}
        </Link>
      ),
    },
  ];

  const getColumnsForCategory = (category: CategoryKey): ColumnsType<any> => {
    const { sortCol, sortDir } = currentSort;

    // All uncommon column keys for this tab
    const thisTabColumnKeys = categories[category].map((c) => c.key);

    const categoryColumns = categories[category]
      .filter((col) => !["date", "createdAt", "updatedAt"].includes(col.key))
      .map((col) => {
        const isAmountCol = [
          "invoicesAmount",
          "receiptsAmount",
          "goCardlessTotalAmount",
          "stripeTotalAmount",
        ].includes(col.key);

        const column: any = {
          title: col.title,
          sorter: true,
          showSorterTooltip: false,
          dataIndex: ["stats", col.key],
          key: col.key,
          width: col.width,
          align: isAmountCol ? "right" : "center",
          // Show sortOrder ONLY if:
          // - This column is the sorted column AND
          // - The sorted column belongs to THIS tab (not another tab's column)
          // If a different tab's uncommon column is sorted → show no indicator here
          sortOrder:
            col.key === sortCol && thisTabColumnKeys.includes(sortCol)
              ? sortDir
              : undefined,
          render: isAmountCol
            ? (value: any) => {
                if (value == null || value === 0) return "-";
                return `£${Number(value).toLocaleString("en-GB", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`;
              }
            : (value: any) => (value == null || value === 0 ? "-" : value),
        };

        return column;
      });

    return [...commonColumns, ...categoryColumns];
  };

  return (
    <>
      <Row justify="start" align="middle" style={{ margin: "1px 15px" }}>
        <PageHeader
          item={[{ label: "Practices", path: "/practices" }]}
          style={{ color: "#172B4D" }}
        />
      </Row>

      <Row className="p-3" style={{ backgroundColor: "white" }}>
        <Col span={24}>
          <Row gutter={[8, 8]} className="pb-3" align="middle">
            {/* ================= ADD ================= */}
            <>
              <Col xs={12} sm={12} md={4} lg={3} xl={3} xxl={2}>
                {!isViewOnly ? (
                  <Button block type="primary" onClick={openAddPractice}>
                    <PlusOutlined /> Add Practice
                  </Button>
                ) : null}
              </Col>

              {/* ============= Clean Cache ============ */}
              <Col xs={12} sm={12} md={4} lg={3} xl={3} xxl={2}>
                {!isViewOnly ?
                  <Button
                    block
                    danger
                    type="primary"
                    onClick={() => setDialog({ type: "addCleanCache", params: {} })}
                  >
                    Clean Cache
                  </Button>
                  : null}
              </Col>
            </>


            {/* ================= SEARCH ================= */}
            <Col
              xs={24}
              sm={12}
              md={{ span: 6, offset: 2 }}
              lg={{ span: 4, offset: 8 }}
              xl={{ span: 4, offset: 9 }}
              xxl={{ span: 3, offset: 13 }}
            >
              <Search
                placeholder="Search "
                allowClear
                onSearch={(val) =>
                  setStatsFilters((prev) => ({ ...prev, search: val }))
                }
                onChange={(e) =>
                  !e.target.value &&
                  setStatsFilters((prev) => ({ ...prev, search: "" }))
                }
                style={{ width: "100%" }}
              />
            </Col>

            {/* ============ Date Range Filter ============ */}
            <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4}>
              <CustomRangePicker
                value={[statsFilters.fromDate, statsFilters.toDate]}
                allowClear={false}
                onChange={(dates) => {
                  setStatsFilters((prev) => ({
                    ...prev,
                    fromDate: dates?.[0] || null,
                    toDate: dates?.[1] || null,
                  }));
                }}
              />
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            items={Object.keys(categories).map((cat) => ({
              key: cat,
              label: cat,
              children: (
                <Table
                  dataSource={dailyPracticeStatsData?.items || []}
                  columns={getColumnsForCategory(cat as CategoryKey)}
                  size="small"
                  loading={{
                    spinning: loading,
                    indicator: <Spin indicator={<LoadingOutlined />} />,
                  }}
                  className="border-top-table"
                  scroll={{
                    x: "max-content",
                    ...((dailyPracticeStatsData?.items?.length || 0) > 15
                      ? { y: "calc(100vh - 340px)" }
                      : {}),
                  }}
                  pagination={{
                    current: pagingItems.currentPage,
                    pageSize: pagingItems.pageSize,
                    total: dailyPracticeStatsData?.totalRecords || 0,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                    showSizeChanger: true,
                    pageSizeOptions: ["15", "25", "50", "100"],
                  }}
                  onChange={handleTableChange}
                />
              ),
            }))}
          />
        </Col>
      </Row>

      {dialog.type === "addPractice" && (
        <AddPractice
          onDismiss={() => {
            setDialog({ type: "", params: {} });
          }}
        />
      )}

      {dialog.type === "addCleanCache" && (
        <ConfirmationModal
          open={true}
          onNo={() => setDialog({ type: "", params: "" })}
          onYes={async () => {
            try {
              const apiData = await CleanCatchService.postCleanCache();
              if (apiData.result) {
                openNotification("success", "Clean cache sucessfully.");
                setDialog({ type: "", params: "" });
              } else {
                openNotification(
                  "error",
                  apiData?.message ?? "Something went wrong",
                );
                setDialog({ type: "", params: "" });
              }
            } catch (err) {
              openNotification("error", err);
              console.log("error", err);
              setDialog({ type: "", params: "" });
            }
          }}
          text={`Are you sure you want to take a clean cache ?`}
        />
      )}
    </>
  );
};

export default Practices;